import React, { useState } from "react";
import { api, inr } from "../lib/api";
import { X, Lightning, Truck, CheckCircle, Prohibit } from "@phosphor-icons/react";

const inputCls = "w-full h-9 px-3 border border-slate-300 rounded-md text-sm";
const labelCls = "text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block";

const F = ({ label, children, span }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const TRANS_MODES = [["1", "Road"], ["2", "Rail"], ["3", "Air"], ["4", "Ship"]];

function EwbFields({ ewb, setEwb }) {
  const set = (k, v) => setEwb({ ...ewb, [k]: v });
  return (
    <div className="grid grid-cols-3 gap-3">
      <F label="Mode">
        <select value={ewb.trans_mode} onChange={(e) => set("trans_mode", e.target.value)} data-testid="ewb-mode" className={inputCls}>
          {TRANS_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </F>
      <F label="Vehicle No">
        <input value={ewb.vehicle_no} onChange={(e) => set("vehicle_no", e.target.value)} placeholder="UP14AB1234" data-testid="ewb-vehicle" className={`${inputCls} font-mono-num uppercase`} />
      </F>
      <F label="Distance (km, 0=auto)">
        <input type="number" value={ewb.distance} onChange={(e) => set("distance", e.target.value)} data-testid="ewb-distance" className={`${inputCls} font-mono-num`} />
      </F>
      <F label="Transporter ID (GSTIN)">
        <input value={ewb.transporter_id} onChange={(e) => set("transporter_id", e.target.value)} placeholder="Optional if vehicle given" data-testid="ewb-transid" className={`${inputCls} font-mono-num uppercase`} />
      </F>
      <F label="Transporter Name" span={2}>
        <input value={ewb.transporter_name} onChange={(e) => set("transporter_name", e.target.value)} data-testid="ewb-transname" className={inputCls} />
      </F>
    </div>
  );
}

export default function ComplianceModal({ sale: initialSale, onClose, onSaved }) {
  const [sale, setSale] = useState(initialSale);
  const ei = sale.einvoice || {};
  const ewbDoc = sale.ewaybill || {};
  const hasIrn = ei.irn && ei.status !== "cancelled";
  const isB2B = !!(sale.customer_gstin || "").trim();
  const over50k = (sale.grand_total || 0) > 50000;
  const hasEwb = !!(ei.ewb_no || ewbDoc.ewb_no);

  const [buyer, setBuyer] = useState({
    gstin: sale.customer_gstin || "",
    legal_name: sale.customer_name || "",
    address: sale.customer_address || "",
    city: "", pin: "",
    state_code: sale.customer_state_code || (sale.customer_gstin || "").slice(0, 2),
  });
  const [withEwb, setWithEwb] = useState(false);
  const [ewb, setEwb] = useState({ trans_mode: "1", vehicle_no: "", distance: 0, transporter_id: "", transporter_name: "", vehicle_type: "R", trans_doc_no: "", trans_doc_dt: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");

  const setB = (k, v) => setBuyer({ ...buyer, [k]: v });

  const run = async (fn) => {
    setBusy(true); setError("");
    try {
      await fn();
      // Refresh the sale so the modal shows the new IRN/EWB state; keep it open
      const r = await api.get(`/store/sales/${sale.id}`);
      setSale(r.data);
      setShowCancel(false);
      onSaved(); // refreshes the invoice list behind the modal
    }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const generateIrn = () => run(() => api.post(`/store/sales/${sale.id}/einvoice`, {
    buyer, ewb: withEwb ? { ...ewb, distance: parseInt(ewb.distance) || 0 } : null,
  }));
  const generateEwbOnIrn = () => run(() => api.post(`/store/sales/${sale.id}/einvoice/ewb`, {
    ewb: { ...ewb, distance: parseInt(ewb.distance) || 0 },
  }));
  const generateStandaloneEwb = () => run(() => api.post(`/store/sales/${sale.id}/ewaybill`, {
    buyer, ewb: { ...ewb, distance: parseInt(ewb.distance) || 0 },
  }));
  const cancelIrn = () => run(() => api.post(`/store/sales/${sale.id}/einvoice/cancel`, {
    reason: 2, remarks: cancelRemarks || "Data entry mistake",
  }));

  const buyerFields = (needGstin) => (
    <div className="grid grid-cols-3 gap-3">
      {needGstin && (
        <F label="Buyer GSTIN *">
          <input value={buyer.gstin} onChange={(e) => setB("gstin", e.target.value.toUpperCase())} data-testid="einv-buyer-gstin" className={`${inputCls} font-mono-num`} />
        </F>
      )}
      <F label="Legal Name *" span={needGstin ? 1 : 2}>
        <input value={buyer.legal_name} onChange={(e) => setB("legal_name", e.target.value)} data-testid="einv-buyer-name" className={inputCls} />
      </F>
      <F label="State Code *">
        <input value={buyer.state_code} onChange={(e) => setB("state_code", e.target.value)} data-testid="einv-buyer-state" className={`${inputCls} font-mono-num`} />
      </F>
      <F label="Address *" span={2}>
        <input value={buyer.address} onChange={(e) => setB("address", e.target.value)} data-testid="einv-buyer-address" className={inputCls} />
      </F>
      <F label="City *">
        <input value={buyer.city} onChange={(e) => setB("city", e.target.value)} data-testid="einv-buyer-city" className={inputCls} />
      </F>
      <F label="PIN Code *">
        <input value={buyer.pin} onChange={(e) => setB("pin", e.target.value)} data-testid="einv-buyer-pin" className={`${inputCls} font-mono-num`} />
      </F>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="compliance-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2"><Lightning size={18} weight="fill" className="text-amber-500" /> GST Compliance</div>
            <div className="text-xs text-slate-500 font-mono-num">{sale.invoice_no} · {inr(sale.grand_total)} · {isB2B ? "B2B" : "B2C"}{over50k ? " · above ₹50k" : ""}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {ei.sandbox && hasIrn && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold rounded-md p-2" data-testid="sandbox-badge">SANDBOX e-invoice — not filed with the government</div>}

          {/* Existing IRN details */}
          {hasIrn && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 space-y-1" data-testid="irn-details">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-sm"><CheckCircle size={15} weight="fill" /> e-Invoice Generated</div>
              <div className="text-[11px] font-mono-num text-slate-700 break-all">IRN: {ei.irn}</div>
              <div className="text-[11px] font-mono-num text-slate-700">Ack No: {ei.ack_no} · {ei.ack_dt}</div>
              {ei.ewb_no && <div className="text-[11px] font-mono-num text-slate-700" data-testid="irn-ewb">E-Way Bill: <b>{ei.ewb_no}</b> · valid till {ei.ewb_valid_till || "-"}</div>}
              <div className="text-[10px] text-slate-500 pt-1">IRN &amp; QR code now print automatically on the invoice PDF.</div>
            </div>
          )}
          {ei.status === "cancelled" && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2" data-testid="irn-cancelled">Previous IRN was cancelled ({ei.cancel_reason || "no reason"}). A new e-invoice can be generated.</div>
          )}
          {!hasIrn && ewbDoc.ewb_no && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm" data-testid="standalone-ewb-details">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold"><Truck size={15} weight="fill" /> E-Way Bill: {ewbDoc.ewb_no}</div>
              <div className="text-[11px] text-slate-600 font-mono-num">Valid till {ewbDoc.ewb_valid_till || "-"}</div>
            </div>
          )}

          {/* Generate e-invoice (B2B, no active IRN) */}
          {!hasIrn && isB2B && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Generate e-Invoice (IRN)</div>
              {buyerFields(true)}
              {over50k && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={withEwb} onChange={(e) => setWithEwb(e.target.checked)} data-testid="einv-with-ewb" className="w-4 h-4" />
                    <span className="text-xs font-semibold text-slate-700">Also generate E-Way Bill (invoice is above ₹50,000)</span>
                  </label>
                  {withEwb && <div className="mt-3"><EwbFields ewb={ewb} setEwb={setEwb} /></div>}
                </div>
              )}
              <button onClick={generateIrn} disabled={busy} data-testid="einv-generate-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
                <Lightning size={14} weight="fill" /> {busy ? "Contacting GSTZen…" : withEwb ? "Generate IRN + E-Way Bill" : "Generate IRN"}
              </button>
            </div>
          )}

          {/* Standalone EWB for B2C > 50k */}
          {!hasIrn && !isB2B && over50k && !ewbDoc.ewb_no && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Generate E-Way Bill (B2C, above ₹50k)</div>
              <div className="text-[11px] text-slate-500">e-Invoicing applies to B2B only. This bill has no buyer GSTIN, so only an E-Way Bill is generated.</div>
              {buyerFields(false)}
              <EwbFields ewb={ewb} setEwb={setEwb} />
              <button onClick={generateStandaloneEwb} disabled={busy} data-testid="ewb-standalone-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
                <Truck size={14} weight="bold" /> {busy ? "Contacting GSTZen…" : "Generate E-Way Bill"}
              </button>
            </div>
          )}

          {/* EWB on existing IRN */}
          {hasIrn && !hasEwb && over50k && (
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Add E-Way Bill to this e-Invoice</div>
              <EwbFields ewb={ewb} setEwb={setEwb} />
              <button onClick={generateEwbOnIrn} disabled={busy} data-testid="ewb-on-irn-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
                <Truck size={14} weight="bold" /> {busy ? "Contacting GSTZen…" : "Generate E-Way Bill"}
              </button>
            </div>
          )}

          {/* Cancel IRN */}
          {hasIrn && (
            <div className="border-t border-slate-200 pt-4">
              {!showCancel ? (
                <button onClick={() => setShowCancel(true)} data-testid="irn-cancel-open" className="text-xs font-semibold text-red-600 hover:underline inline-flex items-center gap-1"><Prohibit size={13} /> Cancel this IRN (within 24h, no active EWB)</button>
              ) : (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className={labelCls}>Cancellation remarks</label>
                    <input value={cancelRemarks} onChange={(e) => setCancelRemarks(e.target.value)} data-testid="irn-cancel-remarks" className={inputCls} placeholder="e.g. Wrong buyer details" />
                  </div>
                  <button onClick={cancelIrn} disabled={busy} data-testid="irn-cancel-confirm" className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-md disabled:opacity-50">{busy ? "Cancelling…" : "Confirm Cancel"}</button>
                </div>
              )}
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-3 whitespace-pre-wrap" data-testid="compliance-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}
